"use client"

import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import OuterFrame from "../../common/OuterFrame"
import { Oval } from "react-loader-spinner"
import "./index.css"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import SidebarOverlay from "../../Sidebar"
import ConcentForm from "../../common/PaitentConcent"
import {
  faUser,
  faRuler,
  faEnvelope,
  faMapMarkerAlt,
  faPhone,
  faIdBadge,
  faBirthdayCake,
  faVenusMars,
  faWeight,
  faBalanceScale,
  faHeartbeat,
  faArrowUp,
  faArrowDown,
  faLungs,
} from "@fortawesome/free-solid-svg-icons"
import PopupContainer from "../../PopupContainer"

// API Attributes index mapping:
// [0] Physical Attributes
// [1] Heart Health attributes
// [2] Lifestyle
// [3] Family History      — familyHistory (select + checkbox sub-options), family_heart_disease (select)
// [4] Medical History     — diabetes (select), dyslipidemia (select + nested checkbox sub-options), osa (select)
// [5] Lab History

const HypertensionComponent = (props) => {
  const navigate = useNavigate()
  const [loaderstatus, setLoaderStatus] = useState(true)
  const [isDataReady, setIsDataReady] = useState(false)
  const [patientInfo, setPatientInfo] = useState({})
  const [formData, setFormData] = useState({})
  const [warningMessage, setWarningMessage] = useState("")
  const [validationErrors, setValidationErrors] = useState({})
  const [activeId, setActiveId] = useState(null)
  const [isChecked1, setIsChecked1] = useState(false)
  const [isPopupOpen, setIsPopupOpen] = useState(false)

  const isEmptyValue = (value) => value === undefined || value === null || value === ""

  const validateSection = (fields, errors) => {
    let isValid = true
    fields?.forEach((field) => {
      if (field.required === "yes" && isEmptyValue(formData[field.key])) {
        errors[field.key] = "This field is required"
        isValid = false
      }
      if (field.ranges && formData[field.key] && formData[field.key] !== "") {
        const numValue = Number(formData[field.key])
        if (numValue < field.ranges[0] || numValue > field.ranges[1]) {
          errors[field.key] = `Value must be between ${field.ranges[0]} and ${field.ranges[1]}`
          isValid = false
        }
      }
    })
    return isValid
  }

  const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,}$/
  const isValidEmail = (v) => EMAIL_REGEX.test(String(v).trim()) && !String(v).includes("..")

  const validateForm = () => {
    const errors = {}
    let isValid = true

    if (Object.keys(validationErrors).length > 0) isValid = false

    // Patient Details
    patientInfo.Patient_Details?.forEach((field) => {
      if (field.required === "yes" && isEmptyValue(formData[field.key])) {
        errors[field.key] = "This field is required"
        isValid = false
      }
      if (/^e-?mail/i.test(field.key) && formData[field.key]) {
        if (!isValidEmail(formData[field.key])) {
          errors[field.key] = "Please enter a valid email address (e.g., user@example.com)"
          isValid = false
        }
      }
      if (field.key === "phone" && formData[field.key]) {
        if (!/^(\+91)?[6-9]\d{9}$/.test(formData[field.key])) {
          errors[field.key] = "Please enter a valid phone number"
          isValid = false
        }
      }
      if (field.ranges && formData[field.key] && formData[field.key] !== "") {
        const numValue = Number(formData[field.key])
        if (numValue < field.ranges[0] || numValue > field.ranges[1]) {
          errors[field.key] = `Value must be between ${field.ranges[0]} and ${field.ranges[1]}`
          isValid = false
        }
      }
    })

    if (!validateSection(patientInfo.Attributes?.[0]?.["Physical Attributes"], errors)) isValid = false
    if (!validateSection(patientInfo.Attributes?.[1]?.["Heart Health attributes"], errors)) isValid = false
    if (!validateSection(patientInfo.Attributes?.[2]?.["Lifestyle"], errors)) isValid = false
    if (!validateSection(patientInfo.Attributes?.[3]?.["Family History"], errors)) isValid = false
    if (!validateSection(patientInfo.Attributes?.[4]?.["Medical History"], errors)) isValid = false
    if (!validateSection(patientInfo.Attributes?.[5]?.["Lab History"], errors)) isValid = false

    setValidationErrors(errors)
    return isValid
  }

  const toggleSidebar = (id) => setActiveId(activeId === id ? null : id)

  const handleSubmit = (event) => {
    event.preventDefault()
    setWarningMessage("")
    if (!validateForm()) {
      setWarningMessage("Please fill in all required fields and ensure all values are within the specified ranges")
      return
    }
    props.form_details && props.form_details(formData)
    navigate(`/hypertension/report`)
  }

  const findFieldByKey = (key) => {
    const sections = [
      patientInfo.Patient_Details,
      patientInfo.Attributes?.[0]?.["Physical Attributes"],
      patientInfo.Attributes?.[1]?.["Heart Health attributes"],
      patientInfo.Attributes?.[2]?.["Lifestyle"],
      patientInfo.Attributes?.[3]?.["Family History"],
      patientInfo.Attributes?.[4]?.["Medical History"],
      patientInfo.Attributes?.[5]?.["Lab History"],
    ]
    for (const section of sections) {
      const field = section?.find((f) => f.key === key)
      if (field) return field
    }
    return null
  }

  const clearError = (key) => {
    setValidationErrors((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  const handleInputChange = (key, value) => {
    const field = findFieldByKey(key)

    if (/^e-?mail/i.test(key)) {
      if (value && !isValidEmail(value)) {
        setValidationErrors((prev) => ({ ...prev, [key]: "Please enter a valid email address (e.g., user@example.com)" }))
      } else { clearError(key) }
    }

    if (key === "phone") {
      if (value && !/^(\+91)?[6-9]\d{9}$/.test(value)) {
        setValidationErrors((prev) => ({ ...prev, [key]: "Please enter a valid phone number" }))
      } else { clearError(key) }
    }

    if (field?.ranges && value !== "") {
      const numValue = Number(value)
      if (isNaN(numValue)) {
        setValidationErrors((prev) => ({ ...prev, [key]: "Please enter a valid number" }))
      } else if (numValue < field.ranges[0] || numValue > field.ranges[1]) {
        setValidationErrors((prev) => ({ ...prev, [key]: `Value must be between ${field.ranges[0]} and ${field.ranges[1]}` }))
      } else { clearError(key) }
    } else if (field?.ranges && value === "") {
      clearError(key)
    }

    setFormData((prev) => ({ ...prev, [key]: value }))
  }

  const handleBlur = (key) => {
    const field = findFieldByKey(key)
    const value = formData[key]

    if (/^e-?mail/i.test(key) && value && !isValidEmail(value)) {
      setFormData((prev) => ({ ...prev, [key]: "" }))
      clearError(key)
      return
    }
    if (key === "phone" && value && !/^(\+91)?[6-9]\d{9}$/.test(value)) {
      setFormData((prev) => ({ ...prev, [key]: "" }))
      clearError(key)
      return
    }
    if (field?.ranges && value !== "" && value !== undefined && value !== null) {
      const numValue = Number(value)
      if (
        isNaN(numValue) ||
        numValue < field.ranges[0] ||
        numValue > field.ranges[1] ||
        (key.toLowerCase() === "age" && !Number.isInteger(numValue))
      ) {
        setFormData((prev) => ({ ...prev, [key]: "" }))
        clearError(key)
      }
    }
  }

  const handleOptionChange = (key, value) => {
    clearError(key)
    setFormData((prev) => ({ ...prev, [key]: value }))
  }

  // CKD-style checkbox handler — stores selections as arrays under the shared key
  const handleCheckboxChangeForForm = (key, value) => {
    setFormData((prev) => {
      const current = Array.isArray(prev[key]) ? prev[key] : []
      let newValues
      if (value === "None") {
        newValues = ["None"]
      } else {
        const filtered = current.filter((v) => v !== "None")
        newValues = filtered.includes(value)
          ? filtered.filter((v) => v !== value)
          : [...filtered, value]
      }
      return { ...prev, [key]: newValues }
    })
  }

  useEffect(() => {
    const initializeData = async () => {
      setLoaderStatus(true)
      try {
        const savedContent = JSON.parse(localStorage.getItem("fields"))
        if (savedContent) {
          setPatientInfo(savedContent)
          setIsDataReady(true)
        }
      } catch (error) {
        console.error("Error loading data:", error)
      } finally {
        setTimeout(() => setLoaderStatus(false), 500)
      }
    }
    initializeData()
  }, [props.app])

  const calculateBMI = useCallback((height, weight) => {
    if (height && weight) {
      let bmi = weight / Math.pow(height * 0.01, 2)
      const decimal = bmi - Math.floor(bmi)
      bmi = Math.floor(bmi) + Math.floor(decimal * 100) / 100
      return bmi
    }
    return null
  }, [])

  useEffect(() => {
    const height = formData.height
    const weight = formData.weight
    if (height && weight) {
      const bmi = calculateBMI(height, weight)
      setFormData((prev) => (prev.BMI !== bmi ? { ...prev, BMI: bmi } : prev))
    }
  }, [formData.height, formData.weight, calculateBMI])

  const getIconForField = (fieldName) => {
    switch ((fieldName || "").toLowerCase()) {
      case "email": return faEnvelope
      case "location": return faMapMarkerAlt
      case "phone": return faPhone
      case "uhid": return faIdBadge
      case "name": return faUser
      case "age": return faBirthdayCake
      case "gender": return faVenusMars
      case "height": return faRuler
      case "weight": return faWeight
      case "bmi": return faBalanceScale
      case "heart rate": return faHeartbeat
      case "systolic bp": return faArrowUp
      case "diastolic bp": return faArrowDown
      case "respiration rate": return faLungs
      default: return faUser
    }
  }

  const getPlaceholderText = (field) => field.ranges ? `${field.ranges[0]} - ${field.ranges[1]}` : ""

  const renderDescriptionButton = (item) => {
    if (!item.description || item.description === "") return null
    return (
      <span>
        <button type="button" onClick={() => toggleSidebar(item.name)} className="description-bar">i</button>
        {activeId === item.name && (
          <SidebarOverlay
            id={item.name}
            description={item.description}
            activeId={activeId}
            name={item.name}
            setActiveId={setActiveId}
            toggleSidebar={toggleSidebar}
          />
        )}
      </span>
    )
  }

  // ─── Number / Text fields (Physical, Lab) ───────────────────────────────────
  const renderAttributes = (attributes) => {
    if (!attributes) return null
    return attributes.map((attribute, index) => (
      <div key={attribute.key} className="form-field-container">
        <div key={index} className="form-field">
          <FontAwesomeIcon icon={getIconForField(attribute.name)} className="field-icon" />
          <>
            <input
              type={attribute.type === "number" ? "number" : "text"}
              placeholder=""
              value={formData[attribute.key] || ""}
              min={attribute.type === "number" ? "0" : undefined}
              step={attribute.type === "number" ? "any" : undefined}
              onFocus={(e) => { e.target.placeholder = getPlaceholderText(attribute) }}
              onBlur={(e) => { e.target.placeholder = ""; handleBlur(attribute.key) }}
              onChange={(e) => handleInputChange(attribute.key, e.target.value)}
              readOnly={attribute.key === "bmi" || attribute.key === "BMI"}
              className={`${validationErrors[attribute.key] ? "error" : ""} ${attribute.key === "bmi" || attribute.key === "BMI" ? "readonly" : ""}`}
            />
            <label>
              {attribute.name} {attribute.units && `(${attribute.units})`}
              {attribute.required === "yes" && <span className="required">*</span>}
            </label>
            {renderDescriptionButton(attribute)}
          </>
        </div>
        {validationErrors[attribute.key] && (
          <span className="error-message">{validationErrors[attribute.key]}</span>
        )}
      </div>
    ))
  }

  // ─── Heart Health: number inputs + radio/select as dropdowns ────────────────
  const renderHealthAttributes = (HeartHealth) => {
    if (!HeartHealth) return null
    const numberItems = HeartHealth.filter((item) => item.type === "number")
    const optionItems = HeartHealth.filter((item) => item.type === "radio" || item.type === "select")

    return (
      <div className="lifestyle-form">
        <div className="lifestyle-form-grid">
          {optionItems.map((item) => (
            <div key={item.key} className="lifestyle-card">
              <p className="lifestyle-title">
                {item.name}
                {item.required === "yes" && <span className="required">*</span>}
                {renderDescriptionButton(item)}
              </p>
              <div className="radio-group">
                <select
                  name={item.key}
                  value={formData[item.key] || ""}
                  onChange={(e) => handleOptionChange(item.key, e.target.value)}
                  className={validationErrors[item.key] ? "error" : ""}
                >
                  <option value="">Select</option>
                  {item.options.map((option, optIndex) => (
                    <option key={optIndex} value={option.value}>{option.name}</option>
                  ))}
                </select>
                {validationErrors[item.key] && <span className="error-message">{validationErrors[item.key]}</span>}
              </div>
            </div>
          ))}
        </div>
        <div className="lifestyle-number-grid">
          {numberItems.map((item) => (
            <div key={item.key} className="form-field-container">
              <div className="form-field">
                <FontAwesomeIcon icon={getIconForField(item.name)} className="field-icon" />
                <input
                  type="number"
                  placeholder=""
                  value={formData[item.key] || ""}
                  min="0"
                  step="any"
                  onFocus={(e) => { e.target.placeholder = getPlaceholderText(item) }}
                  onBlur={(e) => { e.target.placeholder = ""; handleBlur(item.key) }}
                  onChange={(e) => handleInputChange(item.key, e.target.value)}
                  className={validationErrors[item.key] ? "error" : ""}
                />
                <label>
                  {item.name} {item.units && `(${item.units})`}
                  {item.required === "yes" && <span className="required">*</span>}
                </label>
              </div>
              {validationErrors[item.key] && <span className="error-message">{validationErrors[item.key]}</span>}
            </div>
          ))}
        </div>
      </div>
    )
  }

  // ─── Lifestyle: radio buttons for type=radio, dropdown for type=select ──────
  const renderLifeStyle = (attributes) => {
    if (!attributes) return null
    return (
      <div className="lifestyle-aicvd-form-grid">
        {attributes.map((item) => (
          <div key={item.key} className="lifestyle-card">
            <p className="lifestyle-title">
              {item.name}
              {item.required === "yes" && <span className="required">*</span>}
              {renderDescriptionButton(item)}
            </p>

            {item.type === "select" ? (
              <div className="radio-group">
                <select
                  name={item.key}
                  value={formData[item.key] || ""}
                  onChange={(e) => handleOptionChange(item.key, e.target.value)}
                  className={validationErrors[item.key] ? "error" : ""}
                >
                  <option value="">Select</option>
                  {item.options.map((option, optIndex) => (
                    <option key={optIndex} value={option.value}>{option.name}</option>
                  ))}
                </select>
              </div>
            ) : (
              item.options && item.options.map((option) => (
                <div className="sub-option-radio aicvd-radio-options" key={`${item.key}-${option.value}`}>
                  <input
                    type="radio"
                    className="radio-button"
                    id={`${item.key}-${option.value}`}
                    name={item.key}
                    value={option.value}
                    checked={formData[item.key] === option.value}
                    onChange={() => handleOptionChange(item.key, option.value)}
                  />
                  <span className="c-radio"></span>
                  <label htmlFor={`${item.key}-${option.value}`} className="sub-option-radio-label">
                    {option.name}
                  </label>
                </div>
              ))
            )}

            {validationErrors[item.key] && <span className="error-message">{validationErrors[item.key]}</span>}
          </div>
        ))}
      </div>
    )
  }

  // ─── Family History & Medical History ───────────────────────────────────────
  // Matches the exact CKD component pattern:
  //   .card + .select-group + .checkbox-input-box + .custom-checkbox + .sub-option-container
  const renderSelectFields = (attributes) => {
    if (!attributes) return null
    return attributes.map((item) => (
      <div key={item.key} className="card">
        <p>
          {item.name}
          {item.required === "yes" && <span className="required-asterisk">*</span>}
          {renderDescriptionButton(item)}
        </p>

        <div className="select-group">
          {/* Main select dropdown */}
          <select
            name={item.key}
            value={formData[item.key] || ""}
            onChange={(e) => handleOptionChange(item.key, e.target.value)}
            className={validationErrors[item.key] ? "error" : ""}
          >
            <option value="">Select</option>
            {item.options?.map((option, optIndex) => (
              <option key={optIndex} value={option.value}>{option.name}</option>
            ))}
          </select>
          {validationErrors[item.key] && (
            <span className="error-message" style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
              {validationErrors[item.key]}
            </span>
          )}

          {/* Checkbox sub-options — shown for the currently selected option */}
          {item.options?.map((option) =>
            formData[item.key] === option.value &&
            option.sub_options?.map((subOption) => (
              <div key={subOption.value}>
                {/* Level-1 checkbox */}
                <div className="sub-option">
                  <div className="checkbox-input-box checkbox_input_box_in_input_component">
                    <div className="checkbox-container">
                      <input
                        name={subOption.key}
                        id={`${subOption.key}_${subOption.name}`}
                        type="checkbox"
                        value={subOption.value}
                        onChange={() => handleCheckboxChangeForForm(subOption.key, subOption.value)}
                        className="checkbox-input"
                        checked={Array.isArray(formData[subOption.key]) && formData[subOption.key].includes(subOption.value)}
                      />
                      <span className="custom-checkbox">
                        <img src="/checkmark-outline-white.svg" alt="checkmark-outline-white" />
                      </span>
                    </div>
                    <div className="inputbox checkbox_lable_box_input_in_input_component">
                      <label
                        className="lable_in_input_component checkbox_lable_in_input_component"
                        htmlFor={`${subOption.key}_${subOption.name}`}
                      >
                        {subOption.value}
                      </label>
                    </div>
                  </div>
                </div>

                {/* Level-2 nested checkboxes — shown when level-1 is checked */}
                {Array.isArray(formData[subOption.key]) &&
                  formData[subOption.key].includes(subOption.value) &&
                  subOption.sub_options && (
                    <div className="sub-option-container">
                      {subOption.sub_options.map((subSubOption) => (
                        <div key={subSubOption.value} className="sub-option-container">
                          <div className="checkbox-input-box checkbox_input_box_in_input_component">
                            <div className="checkbox-container">
                              <input
                                name={subSubOption.key}
                                id={`${subOption.key}_${subSubOption.name}`}
                                type="checkbox"
                                value={subSubOption.value}
                                onChange={() => handleCheckboxChangeForForm(subSubOption.key, subSubOption.value)}
                                className="checkbox-input"
                                checked={Array.isArray(formData[subSubOption.key]) && formData[subSubOption.key].includes(subSubOption.value)}
                              />
                              <span className="custom-checkbox">
                                <img src="/checkmark-outline-white.svg" alt="checkmark-outline-white" />
                              </span>
                            </div>
                            <div className="inputbox checkbox_lable_box_input_in_input_component">
                              <label
                                className="lable_in_input_component checkbox_lable_in_input_component"
                                htmlFor={`${subOption.key}_${subSubOption.name}`}
                              >
                                {subSubOption.value}
                              </label>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            ))
          )}
        </div>
      </div>
    ))
  }

  const handleCheckboxChange = (checkboxId) => {
    if (checkboxId === 1) setIsChecked1((prev) => !prev)
  }

  const openPopup = () => setIsPopupOpen(true)
  const closePopup = () => setIsPopupOpen(false)

  return (
    <OuterFrame
      hightlight="2"
      appName={"AI HYPERTENSION"}
      doc_name="hypertension"
      color={props.color}
      secondaryColor={props.secondaryColor}
      surface_color={props.surface_color}
      surface_highlight_color={props.surface_highlight_color}
      surface_hover_color={props.surface_hover_color}
      button_hover_color={props.button_hover_color}
      component={
        loaderstatus || !isDataReady ? (
          <div className="loader-container">
            <Oval
              height={80}
              width={80}
              color="#4fa94d"
              visible={true}
              ariaLabel="oval-loading"
              secondaryColor="#4fa94d"
              strokeWidth={2}
              strokeWidthSecondary={2}
            />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="medical-form">
            <div className="form-sections">

              {/* ── Basic Information ── */}
              <section className="basic-info">
                <h2 className="patient-medical-info-heading">Basic Information</h2>
                <div className="form-grid">
                  {patientInfo?.Patient_Details.map((patient, index) => (
                    <div key={patient.key} className="form-field-container">
                      <div key={index} className="form-field">
                        <FontAwesomeIcon icon={getIconForField(patient.name)} className="field-icon" />
                        {patient.key === "gender" ? (
                          <select
                            value={formData.gender || ""}
                            onChange={(e) => handleInputChange("gender", e.target.value)}
                            className="gender-select"
                          >
                            <option value="" disabled>Select Gender</option>
                            {patient.options.map((option) => (
                              <option key={option.value} value={option.value}>{option.name}</option>
                            ))}
                          </select>
                        ) : (
                          <>
                            <input
                              type={patient.type === "number" ? "number" : "text"}
                              placeholder=""
                              value={formData[patient.key] || ""}
                              min={patient.type === "number" ? "0" : undefined}
                              step={patient.type === "number" ? (patient.key === "age" ? "1" : "any") : undefined}
                              onFocus={(e) => { e.target.placeholder = getPlaceholderText(patient) }}
                              onBlur={(e) => { e.target.placeholder = ""; handleBlur(patient.key) }}
                              onChange={(e) => handleInputChange(patient.key, e.target.value)}
                              className={validationErrors[patient.key] ? "error" : ""}
                            />
                            <label>
                              {patient.name} {patient.required === "yes" && <span className="required">*</span>}
                            </label>
                          </>
                        )}
                      </div>
                      {validationErrors[patient.key] && (
                        <span className="error-message">{validationErrors[patient.key]}</span>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* ── Physical Attributes [0] ── */}
              <section className="physical-attributes">
                <h2 className="patient-medical-info-heading">Physical Attributes</h2>
                <div className="form-grid">
                  {renderAttributes(patientInfo?.Attributes?.[0]?.["Physical Attributes"])}
                </div>
              </section>

              {/* ── Heart Health Attributes [1] ── */}
              <section className="physical-attributes">
                <h2 className="patient-medical-info-heading">Heart Health Attributes</h2>
                {renderHealthAttributes(patientInfo?.Attributes?.[1]?.["Heart Health attributes"])}
              </section>

              {/* ── Lifestyle [2] ── */}
              <section className="physical-attributes">
                <h2 className="patient-medical-info-heading">Lifestyle</h2>
                {renderLifeStyle(patientInfo?.Attributes?.[2]?.["Lifestyle"])}
              </section>

              {/* ── Family History [3] ── */}
              <section className="physical-attributes">
                <h2 className="patient-medical-info-heading">Family History</h2>
                <div className="medical-form-grid">
                  {renderSelectFields(patientInfo?.Attributes?.[3]?.["Family History"])}
                </div>
              </section>

              {/* ── Medical History [4] ── */}
              <section className="physical-attributes">
                <h2 className="patient-medical-info-heading">Medical History</h2>
                <div className="medical-form-grid">
                  {renderSelectFields(patientInfo?.Attributes?.[4]?.["Medical History"])}
                </div>
              </section>

              {/* ── Lab History [5] ── */}
              <section className="lab-history">
                <h2 className="patient-medical-info-heading">Lab History</h2>
                <div className="form-grid">
                  {renderAttributes(patientInfo?.Attributes?.[5]?.["Lab History"])}
                </div>
              </section>

              <div className="showConcentDiv">
                <span className="DocLogoForConcentButton" onClick={openPopup}>
                  <img src="/doc-icon.svg" className="consent-icon" alt="consent-icon" />
                </span>
                <button className="showConcentButton" onClick={openPopup} type="button">
                  PATIENT INFORMATION SHEET AND INFORMED CONSENT
                </button>
              </div>
              <div className="checkBoxForTC">
                <div className="checkbox-for-patient-consent">
                  <div className="checkbox-container">
                    <input
                      type="checkbox"
                      className="checkbox-input"
                      id="loginCheckBox1"
                      checked={isChecked1}
                      onChange={() => handleCheckboxChange(1)}
                    />
                    <span className="custom-checkbox">
                      <img src="/checkmark-outline-white.svg" alt="checkmark-outline-white" />
                    </span>
                  </div>
                  <label htmlFor="loginCheckBox1" className="patient-consent-label-content">
                    Patient has read and understood the information sheet, had the opportunity to ask questions, and
                    agreed to participate in the NCD Risk Scores at Apollo Hospitals.
                  </label>
                </div>
              </div>
            </div>

            {warningMessage !== "" && <p className="all-fields-warning-msg">{warningMessage}</p>}

            <div className="submit-button-container">
              <button
                type="submit"
                className="submit-button"
                disabled={!isChecked1}
                style={{ opacity: !isChecked1 ? 0.5 : 1, cursor: !isChecked1 ? "not-allowed" : "pointer" }}
              >
                Submit
              </button>
            </div>

            <PopupContainer isOpen={isPopupOpen} onClose={closePopup} title="STUDY PARTICIPANT INFORMATION SHEET">
              <ConcentForm data={patientInfo.patient_consent} />
            </PopupContainer>
          </form>
        )
      }
    />
  )
}

export default HypertensionComponent
